import React, { useContext, useState } from "react";
import { SlidersHorizontal, ChevronDown, ChevronUp } from "lucide-react";
import { ThemeContext } from "../../../../constant/theme";
import { LanguageContext } from "../../../../constant/LanguageContext";
import { useAuth } from "../../../../context/AuthContext";
import text from "../../constants/reportsTranslations";
import FilterField from "./FilterField";

/** Sticky filter bar for the selected report — full-width, fields flow in a
 * responsive grid, only renders fields relevant to the active category
 * (category.filterFields), so Payroll shows a month picker while Attendance
 * shows exact-date-vs-range, per the plan's per-type filtering. */
const FiltersPanel = ({ category, draft, setField, apply, reset, options, optionsLoading }) => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { isFeatureVisible } = useAuth();
  const isDark = theme === "dark";
  const isRTL = language === "ar";
  const [collapsed, setCollapsed] = useState(false);

  // dateMode is an internal toggle, not a standalone filter — only its two
  // target fields (specificDate/dateRange) are admin-gated.
  const fields = (category?.filterFields || []).filter(
    (f) => f === "dateMode" || isFeatureVisible?.("reports", `filter_${f}`) !== false
  );
  const hasDateChoice = fields.includes("specificDate") && fields.includes("dateRange");

  if (!fields.length) return null;

  // ReusableMultiSelect (used by the Employee/Company "multiple" mode) only
  // commits its selection to `draft` when its dropdown closes (via a
  // mousedown-outside listener), not on every checkbox click. If the user
  // picks items and clicks Apply while that dropdown is still open, Apply
  // could read a stale `draft` from before the last click(s). Firing a
  // synthetic mousedown first forces any open dropdown to close (and thus
  // commit) before apply() reads the filter state.
  const handleApply = () => {
    // Must dispatch FROM an Element (document.body), not `document` itself —
    // some click-outside handlers elsewhere on the page call
    // event.target.closest(...), which throws if target is the document.
    document.body.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
    setTimeout(apply, 0);
  };

  return (
    <div
      style={{
        borderRadius: 18, padding: 18,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"}`,
        background: isDark ? "rgba(15,23,42,0.85)" : "rgba(255,255,255,0.97)",
        backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
        boxShadow: isDark ? "0 8px 26px rgba(0,0,0,0.3)" : "0 8px 26px rgba(15,23,42,0.08)",
        display: "flex", flexDirection: "column", gap: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <SlidersHorizontal size={15} color={isDark ? "#93c5fd" : "#2563eb"} />
          <span style={{ fontSize: 14, fontWeight: 700, color: isDark ? "#f1f5f9" : "#1e293b" }}>{text[language].filtersTitle}</span>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? text[language].filtersExpand : text[language].filtersCollapse}
          style={{ background: "none", border: "none", cursor: "pointer", color: isDark ? "#94a3b8" : "#64748b" }}
        >
          {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </button>
      </div>

      {!collapsed && (
        <>
          {hasDateChoice && (
            <div style={{ display: "flex", width: 220, borderRadius: 10, overflow: "hidden", border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}` }}>
              {["range", "exact"].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setField("dateMode", mode)}
                  style={{
                    flex: 1, padding: "7px 0", border: "none", cursor: "pointer", fontSize: 11.5, fontWeight: 700,
                    background: (draft.dateMode || "range") === mode ? "#3b82f6" : "transparent",
                    color: (draft.dateMode || "range") === mode ? "#fff" : (isDark ? "#94a3b8" : "#64748b"),
                  }}
                >
                  {mode === "range" ? text[language].filterDateModeRange : text[language].filterDateModeExact}
                </button>
              ))}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
            {fields.filter((f) => f !== "dateMode").filter((f) => {
              if (!hasDateChoice) return true;
              const mode = draft.dateMode || "range";
              if (f === "specificDate") return mode === "exact";
              if (f === "dateRange") return mode === "range";
              return true;
            }).map((fieldKey) => (
              <FilterField
                key={fieldKey}
                fieldKey={fieldKey}
                value={draft[fieldKey]}
                onChange={(v) => setField(fieldKey, v)}
                theme={theme}
                isRTL={isRTL}
                text={text}
                language={language}
                options={options}
                optionsLoading={optionsLoading}
              />
            ))}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", flexDirection: isRTL ? "row-reverse" : "row" }}>
            <button
              type="button"
              onClick={handleApply}
              style={{
                padding: "9px 22px", borderRadius: 10, border: "none", cursor: "pointer",
                background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff", fontSize: 12.5, fontWeight: 700,
              }}
            >
              {text[language].filtersApply}
            </button>
            <button
              type="button"
              onClick={reset}
              style={{
                padding: "9px 22px", borderRadius: 10, cursor: "pointer", fontSize: 12.5, fontWeight: 700,
                border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)"}`,
                background: "transparent", color: isDark ? "#e2e8f0" : "#334155",
              }}
            >
              {text[language].filtersReset}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default FiltersPanel;
